import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ReserveLoadingService } from '../../shared/services/reserve-loading.service';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { Router } from '@angular/router';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { BusinessPlanRequest } from '../../shared/interfaces/reserve-loading.model';
import { NgOptimizedImage } from '@angular/common';
import { MemberInfoResponse, ModifyInfoService } from '../../shared/services/modify-info.service';
import Decimal from 'decimal.js';
import { Subscription } from 'rxjs';

/**
 * 方案預算loading頁面
 */
@Component({
  selector: 'app-reserve-loading',
  standalone: true,
  imports: [TranslatePipe, NgOptimizedImage],
  templateUrl: './reserve-loading.component.html',
  styleUrl: './reserve-loading.component.scss',
})
export class ReserveLoadingComponent implements OnInit, OnDestroy {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  reserveLoadingService: ReserveLoadingService = inject(ReserveLoadingService);
  modifyInfoService: ModifyInfoService = inject(ModifyInfoService);
  router: Router = inject(Router);

  /** 倒數秒數 */
  countdown = 60
  /** 是否已超時 */
  isTimeout = false;
  /** 是否顯示倒數區塊（延遲 5 秒後才顯示） */
  showCountdown = false;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private apiSubscription: Subscription | null = null;

  ngOnInit(): void {
    // 將當前預約步驟設置為2.5(步驟條不顯示)
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 2.5);
    });

    // 解決髒資料問題，若是已登入狀態
    if (this.sharedStorageService.get('loginStatus') === 'memberLogin') {
      this.modifyInfoService.getMemberInfo().subscribe({
        next: (res: MemberInfoResponse) => {
          if (res.statusCode === 'S0000') {
            console.log('token有效，資料保留');
          }
        }
      });
    }
    // 啟動倒數計時（延遲 5 秒後才開始顯示和計時）
    setTimeout(() => {
      this.showCountdown = true;
      this.startCountdown();
    }, 5000);

    // 呼叫 API
    this.callPlanApi();
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.apiSubscription?.unsubscribe();
  }

  /** 啟動倒數計時器 */
  private startCountdown(): void {
    this.countdown = 60
    this.isTimeout = false;

    this.countdownTimer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearCountdown();
        this.isTimeout = true;
        // 超時後取消 API 訂閱
        this.apiSubscription?.unsubscribe();
      }
    }, 1000);
  }

  /** 清除計時器 */
  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /** 重新嘗試 */
  retry(): void {
    this.clearCountdown();
    this.apiSubscription?.unsubscribe();
    this.startCountdown();
    this.callPlanApi();
  }

  /** 呼叫方案 API 並處理結果 */
  private callPlanApi(): void {
    const userPlanRequest: UserPlanRequest | null =
      this.sharedStorageService.get('userPlanRequest') ?? null;
    const businessPlanRequest: BusinessPlanRequest | null =
      this.sharedStorageService.get('businessPlanRequest') ?? null;

    if (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.PERSONAL &&
      userPlanRequest
    ) {
      // 個人預約
      this.apiSubscription = this.reserveLoadingService
        .getUserPlanResult(userPlanRequest)
        .subscribe(res => {
          if (res.statusCode === 'S0000' && res.data) {
            // 處理折扣率四捨五入到小數點後五位
            const processedData = {
              ...res.data,
              allCheckup: res.data.allCheckup ? {
                ...res.data.allCheckup,
                discountRate: res.data.allCheckup.discountRate
                  ? Decimal(res.data.allCheckup.discountRate).toFixed(5)
                  : '1.00000'
              } : {
                discountRate: '1.00000',
                discountedPlanPrice: '0',
                abnormalCnt: '0',
                potentialCnt: '0',
                tagList: [],
                packageList: []
              },
              aiExclusive: res.data.aiExclusive ? {
                ...res.data.aiExclusive,
                discountRate: res.data.aiExclusive.discountRate
                  ? Decimal(res.data.aiExclusive.discountRate).toFixed(5)
                  : '1.00000'
              } : {
                discountRate: '1.00000',
                discountedPlanPrice: '0',
                abnormalCnt: '0',
                potentialCnt: '0',
                tagList: [],
                packageList: []
              },
              basicCheckup: res.data.basicCheckup ? {
                ...res.data.basicCheckup,
                discountRate: res.data.basicCheckup.discountRate
                  ? Decimal(res.data.basicCheckup.discountRate).toFixed(5)
                  : '1.00000'
              } : {
                discountRate: '1.00000',
                discountedPlanPrice: '0',
                abnormalCnt: '0',
                potentialCnt: '0',
                tagList: [],
                packageList: []
              }
            };

            this.sharedStorageService.set('userPlanResponse', processedData);

            // API 成功，停止倒數並跳轉
            this.clearCountdown();

            if (this.sharedStorageService.get('healthPlan') === 'special') {
              this.router.navigate(['/healthcheck/healthplan/reserve/personalplan']).then();
            } else {
              this.router.navigate(['/healthcheck/reserve/personalplan']).then();
            }
          }
        });

    } else if (
      this.sharedStorageService.get('reserveType') === ID_LOGIN_TYPE.ENTERPRISE &&
      businessPlanRequest
    ) {
      // 企業預約
      const businessLoginInfo = this.sharedStorageService.get('businessPlanRequest');
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
      const createMemberRequest = this.sharedStorageService.get('createMemberRequest');

      const request: BusinessPlanRequest = {
        orgNo: businessLoginInfo?.orgNo ?? '',
        employeeId: businessLoginInfo?.employeeId ?? '',
        companyNo: businessLoginInfo?.companyNo ?? '',
        gender: businessLoginInfo?.gender ?? '',
        idNo: userPlanRequest?.idNo ?? idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '',
      };

      this.apiSubscription = this.reserveLoadingService
        .getBusinessPlanResult(request)
        .subscribe(res => {
          if (res.data && res.data.packageList) {
            this.sharedStorageService.set('businessPlanResponse', res.data);

            // API 成功，停止倒數並跳轉
            this.clearCountdown();
            this.router.navigate(['/healthcheck/reserve/companyplan']).then();
          }
        });
    }
  }
}
